/**
 * Who is in the sky: which nation flies what, which types existed yet, and the
 * twenty-five named aces.
 *
 * **Nation** is a plain fact from docs/game/aircraft.md §§3–6, which sorts the
 * fleet into Allied and German sections. **Era availability** is the same
 * document's §10 table — the manual's own introduction dates, bucketed into the
 * three enlistment starts the career mode offers (Dec 1915 / Oct 1916 /
 * Jan 1918). A single mission has no calendar, so it names an era instead; the
 * campaign lane replaces this with a real date when it lands.
 *
 * **Port-side simplification, stated up front.** The original picks the enemy
 * flight's type from the campaign's own date and squadron equipment. This table
 * is coarser: three buckets, one list of scouts per side per bucket, drawn at
 * random from the mission seed. It is not a claim about how `PS.EXE` chose.
 *
 * The **ace → aircraft** mapping is likewise port-side. `PS.EXE` confirms each
 * ace is tied to "their historical personal mount" and auto-selects it
 * (docs/game/missions.md § *Related single-player modes*), but the mapping
 * itself is not in any file this project has decoded — only von Richthofen's
 * and Udet's progressions were recovered from `APLANE.DAT`
 * (docs/game/aces-squadrons.md §6). Every other mount below is the aeroplane
 * that ace is historically best known for, restricted to types this fleet
 * actually has.
 */

import { AIRCRAFT_KEYS, DISPLAY_NAMES, type AircraftKey } from "../flight/aircraft-table.ts";
import type { MissionEra } from "./mission-world.ts";

export const NATIONS = ["allied", "german"] as const;

export type Nation = (typeof NATIONS)[number];

export const NATION_LABELS: Readonly<Record<Nation, string>> = { allied: "Allied", german: "German" };

const GERMAN_KEYS: readonly AircraftKey[] = [
  "FOKE3",
  "ALBA_D2",
  "ALBA_D3",
  "ALBA_D5",
  "PFALZ",
  "FOKDR1",
  "FOKD7",
  "FOKD8",
  "HALBD2",
  "SIEMENS",
  "AVIATIK",
  "ROLAND",
  "RUMPLER",
  "GOTHA",
  "JUNKER",
];

export function nationOf(key: AircraftKey): Nation {
  return GERMAN_KEYS.includes(key) ? "german" : "allied";
}

/**
 * The two-seaters, bombers and ground-attack types. `RBMB.EXE` refuses to put
 * the player in one — *"The player may only fly a scout plane."* — so these are
 * AI mounts, escortees and targets, never the player's aeroplane.
 *
 * All eleven of them turn out to have real baked geometry (`VOLUME.005/*.TBL`),
 * which corrects docs/game/missions.md's "no confirmed 3D model anywhere in this
 * repo's data": that claim was made against a loose-file listing and missed the
 * volume archive. See the implementation note appended to that document.
 */
export const TWO_SEATER_KEYS: readonly AircraftKey[] = [
  "BE2",
  "AVIATIK",
  "FE2",
  "ROLAND",
  "RE8",
  "RUMPLER",
  "BRISTOL",
  "DH4",
  "JUNKER",
  "GOTHA",
  "HANDLEY",
];

/** Everything the player may fly, and everything a fighter flight is drawn from. */
export const SCOUT_KEYS: readonly AircraftKey[] = AIRCRAFT_KEYS.filter(
  (key) => !TWO_SEATER_KEYS.includes(key),
);

/** Scouts in service, by side, at each of the three enlistment dates (§10 table). */
const ERA_SCOUTS: Readonly<Record<MissionEra, Readonly<Record<Nation, readonly AircraftKey[]>>>> = {
  early: {
    allied: ["MORANE", "NIEU11", "AIRCO", "NIEUPORT", "SOPPUP"],
    german: ["FOKE3", "HALBD2", "ALBA_D2"],
  },
  mid: {
    allied: ["NIEUPORT", "SOPPUP", "SPAD_7", "SOPTRI", "SE5", "CAMEL"],
    german: ["ALBA_D2", "ALBA_D3", "ALBA_D5", "PFALZ", "FOKDR1"],
  },
  late: {
    allied: ["CAMEL", "SE5", "SPAD_13", "SNIPE", "NIEU28"],
    german: ["FOKD7", "FOKDR1", "ALBA_D5", "PFALZ", "SIEMENS", "FOKD8"],
  },
};

export function scoutsFor(nation: Nation, era: MissionEra): readonly AircraftKey[] {
  return ERA_SCOUTS[era][nation];
}

/** The reconnaissance two-seater a flight of this side and era would be escorting. */
const ERA_RECON: Readonly<Record<MissionEra, Readonly<Record<Nation, AircraftKey>>>> = {
  early: { allied: "BE2", german: "AVIATIK" },
  mid: { allied: "RE8", german: "ROLAND" },
  late: { allied: "BRISTOL", german: "RUMPLER" },
};

export function reconTypeFor(nation: Nation, era: MissionEra): AircraftKey {
  return ERA_RECON[era][nation];
}

export function opposingNation(nation: Nation): Nation {
  return nation === "allied" ? "german" : "allied";
}

export interface FamousAce {
  readonly id: string;
  /** The name `ACE.DAT` shows on the SELECT THE ACE TO DOGFIGHT screen. */
  readonly name: string;
  readonly nation: Nation;
  /** Their historical personal mount, restricted to this fleet. Port-side — see the module note. */
  readonly aircraft: AircraftKey;
  /** One line for the briefing card, drawn from the ace's `ACE.DAT` bio. */
  readonly note: string;
}

function ace(id: string, name: string, nation: Nation, aircraft: AircraftKey, note: string): FamousAce {
  return { id, name, nation, aircraft, note };
}

/**
 * The 25 in `ACE.DAT` order: 11 German, then 14 Allied
 * (docs/game/aces-squadrons.md §1).
 */
export const FAMOUS_ACES: readonly FamousAce[] = [
  ace("richthofen", "Manfred von Richthofen", "german", "FOKDR1", "The Red Baron — 80 victories, and the scarlet Triplane everyone knows him by."),
  ace("udet", "Ernst Udet", "german", "FOKD7", "Germany's second-highest scorer, and a flamboyant painter of his own aeroplanes."),
  ace("voss", "Werner Voss", "german", "FOKDR1", "A natural pilot and a crack shot, lost in an epic solo fight against 56 Squadron."),
  ace("lothar", "Lothar von Richthofen", "german", "ALBA_D5", "The Baron's younger brother — a fierce, impetuous attacker rather than a tactician."),
  ace("goering", "Hermann Goering", "german", "FOKD7", "The last commander of Jagdgeschwader 1, inheriting Richthofen's circus."),
  ace("boelcke", "Oswald Boelcke", "german", "ALBA_D2", "The father of fighter tactics; his Dicta are still the grammar of air combat."),
  ace("immelmann", "Max Immelmann", "german", "FOKE3", "The Eagle of Lille, and the turn that carries his name."),
  ace("loewenhardt", "Erich Loewenhardt", "german", "FOKD7", "Third-highest German scorer, killed in a collision at the height of his run."),
  ace("berthold", "Rudolf Berthold", "german", "FOKD7", "Flew and led with an arm crippled by earlier wounds — the Iron Knight."),
  ace("schleich", "Eduard Ritter von Schleich", "german", "ALBA_D5", "The Black Knight, in an all-black Albatros."),
  ace("degelow", "Karl Degelow", "german", "FOKD7", "The last German airman to receive the Blue Max, marked by a white stag."),
  ace("mannock", "Edward Mannock", "allied", "SE5", "The highest-scoring British ace, and a merciless student of the ambush."),
  ace("bishop", "William Bishop", "allied", "NIEUPORT", "A Canadian marksman whose lone dawn raid on an aerodrome won the VC."),
  ace("mccudden", "James McCudden", "allied", "SE5", "A methodical hunter of high two-seaters, risen from mechanic to major."),
  ace("ball", "Albert Ball", "allied", "SE5", "England's first air hero, who preferred to attack alone and from below."),
  ace("hawker", "Lanoe Hawker", "allied", "AIRCO", "The first British ace, brought down by Richthofen after a long, even fight."),
  ace("fonck", "Rene Fonck", "allied", "SPAD_13", "The Allies' top scorer, and the most economical shot of the war."),
  ace("guynemer", "Georges Guynemer", "allied", "SPAD_13", "France's beloved ace, frail in health and reckless in the air."),
  ace("rickenbacker", "Edward Rickenbacker", "allied", "SPAD_13", "America's ace of aces, a racing driver turned squadron commander."),
  ace("luke", "Frank Luke", "allied", "SPAD_13", "The Arizona Balloon Buster — 14 balloons in eight astonishing days."),
  ace("nungesser", "Charles Nungesser", "allied", "NIEUPORT", "Flew on through a catalogue of injuries, under a skull-and-coffin insignia."),
  ace("collishaw", "Raymond Collishaw", "allied", "SOPTRI", "Led Naval 10's Black Flight of all-black Sopwith Triplanes."),
  ace("maclaren", "Donald Maclaren", "allied", "CAMEL", "A Canadian who scored 54 victories in barely eight months on Camels."),
  ace("barker", "William George Barker", "allied", "SNIPE", "Fought a whole formation alone in a Snipe, wounded three times, and lived."),
  ace("dallas", "Roderic Stanley Dallas", "allied", "SOPTRI", "An Australian Triplane pilot with a taste for practical jokes on the enemy."),
];

export function aceById(id: string): FamousAce | undefined {
  return FAMOUS_ACES.find((entry) => entry.id === id);
}

export function acesFor(nation: Nation): readonly FamousAce[] {
  return FAMOUS_ACES.filter((entry) => entry.nation === nation);
}

export function aircraftLabel(key: AircraftKey): string {
  return DISPLAY_NAMES[key];
}
